<template>
  <!--
    ActionSheet (bottom sheet) com pegada de app nativo (estilo iOS).
    Reutilizável: passe `actions` e escute `@select`. NÃO usa b-modal — é um
    overlay próprio, logo não é afetado pela regra global de modal-fullscreen.

    Uso:
      <action-sheet
        v-model="show"
        title="O que deseja fazer?"
        :actions="[{ key:'a', label:'Opção', icon:'fas fa-qrcode', variant:'primary' }]"
        @select="onSelect"
      />
  -->
  <transition name="sheet">
    <div
      v-if="value"
      class="action-sheet-overlay"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div class="action-sheet">
        <div class="action-sheet-group">
          <div v-if="title" class="action-sheet-title">{{ title }}</div>

          <button
            v-for="(action, i) in actions"
            :key="action.key || i"
            type="button"
            class="action-sheet-item"
            :class="action.variant ? 'is-' + action.variant : ''"
            @click="onSelect(action)"
          >
            <span class="action-sheet-icon" v-if="action.icon">
              <i :class="action.icon"></i>
            </span>
            <span class="action-sheet-text">
              <span class="action-sheet-label">{{ action.label }}</span>
              <small v-if="action.description" class="action-sheet-desc">{{
                action.description
              }}</small>
            </span>
          </button>

          <!-- conteúdo livre opcional -->
          <slot />
        </div>

        <button type="button" class="action-sheet-cancel" @click="close">
          {{ cancelText }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "ActionSheet",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    // v-model: visibilidade
    value: { type: Boolean, default: false },
    title: { type: String, default: "" },
    // [{ key, label, description?, icon?, variant? }]
    actions: { type: Array, default: () => [] },
    cancelText: { type: String, default: "Cancelar" },
  },
  watch: {
    value(open) {
      // Permite fechar com ESC (útil no desktop / testes).
      if (open) {
        document.addEventListener("keydown", this.onKeydown);
      } else {
        document.removeEventListener("keydown", this.onKeydown);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === "Escape") this.close();
    },
    onSelect(action) {
      // Fecha primeiro (a ação geralmente abre outro modal/tela).
      this.$emit("input", false);
      this.$emit("select", action.key, action);
    },
    close() {
      this.$emit("input", false);
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
.action-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1090;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.action-sheet {
  padding: 0 8px;
  // respeita a safe-area de iPhones com notch
  padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.action-sheet-group {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.12);
}

.action-sheet-title {
  text-align: center;
  font-size: 13px;
  color: #8a8a8e;
  padding: 14px 16px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.action-sheet-item {
  width: 100%;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  & + .action-sheet-item {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  &:active {
    background-color: #e9e9ee;
  }
}

.action-sheet-icon {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: #eef0f3;
  color: #007aff;
}

.action-sheet-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.action-sheet-label {
  font-size: 17px;
  font-weight: 600;
  color: #1c1c1e;
}
.action-sheet-desc {
  font-size: 12px;
  color: #8a8a8e;
  margin-top: 2px;
}

// Variantes só colorem o ícone (mantém o item neutro, estilo iOS).
.action-sheet-item.is-primary .action-sheet-icon {
  background: rgba(0, 122, 255, 0.12);
  color: #007aff;
}
.action-sheet-item.is-success .action-sheet-icon {
  background: rgba(52, 199, 89, 0.14);
  color: #34c759;
}
.action-sheet-item.is-secondary .action-sheet-icon {
  background: #eef0f3;
  color: #636366;
}
.action-sheet-item.is-danger .action-sheet-icon {
  background: rgba(255, 59, 48, 0.12);
  color: #ff3b30;
}

.action-sheet-cancel {
  width: 100%;
  margin-top: 8px;
  border: 0;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  font-size: 17px;
  font-weight: 700;
  color: #007aff;
  cursor: pointer;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.12);
  &:active {
    background-color: #e9e9ee;
  }
}

// Transição: overlay faz fade, a folha desliza de baixo.
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.28s ease;
}
.sheet-enter,
.sheet-leave-to {
  opacity: 0;
}
.sheet-enter .action-sheet,
.sheet-leave-to .action-sheet {
  transform: translateY(110%);
}
</style>
